import {delay, distinctUntilChanged, filter, finalize, fromEvent, MonoTypeOperatorFunction, Observable, switchMap, takeUntil, timer} from "rxjs";
import {initDebugger} from "./debug";
import {distinctUntilKeyChanged, map, tap} from "rxjs/operators";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {T} from "@angular/cdk/keycodes";

const debug = initDebugger('observable');

/** Logger melding når subscription avsluttes.
 * For bruk i debugging. */
export function logVedFinalize<T>(melding = ''): MonoTypeOperatorFunction<T> {
    return finalize(() => debug(`${melding} observable finalized (subscription avsluttet)`));
}

/** Logger melding når verdi leveres.
 * For bruk i debugging. */
export function logVerdiLevert<T>(melding = ''): MonoTypeOperatorFunction<T> {
    return tap(verdi => debug(`${melding} verdi levert til observable`, verdi));
}

/** Logger hendelser.
 * For bruk i debugging. */
export function logHendelser<T>(melding = ''): MonoTypeOperatorFunction<T> {
    return tap({
        subscribe: () => debug(`${melding} subscribe`),
        next: verdi => debug(`${melding} next`, verdi),
        unsubscribe: () => debug(`${melding} unsubscribe`),
        finalize: () => debug(`${melding} finalize`),
        error: feil => debug(`${melding} error`, feil),
        complete: () => debug(`${melding} complete`)
    });
}

/** Filtrerer bort null/undefined */
export function filtrerBortNil<T>(): MonoTypeOperatorFunction<T> {
    return filter(verdi => erIkkeNil(verdi));
}

/** Som {@link distinctUntilChanged}, men utfører sammenlikning av forrige og nåværende ved å sammenlikne deres JSON-representasjon.
 * Fra distinctUntilChanged: Returns a result Observable that emits all values pushed by the source observable if they are distinct
 * in comparison to the last value the result observable emitted.*/
export function distinctUntilChangedDeep<T>(): MonoTypeOperatorFunction<T> {
    return distinctUntilChanged(
      (forrige: string, nåværende: string) => forrige == nåværende,
      (verdi: T) => JSON.stringify(verdi)
    );
}

/** Som {@link distinctUntilChanged}, men utfører sammenlikning av forrige og nåværende ved å sammenlikne id-attributtet.
 * Fra distinctUntilChanged: Returns a result Observable that emits all values pushed by the source observable if they are distinct
 * in comparison to the last value the result observable emitted.*/
export function distinctEntitetIdUntilChanged<T extends {id:string}>(): MonoTypeOperatorFunction<T> {
    return distinctUntilKeyChanged('id');
}

/** Pipe-steg som leverer mottatt verdi forsinket med angitt 'forsinkelse',
 * med mindre 'kanselerLeveranse' i dette intervalle leverer en verdi, da vil
 * den forsinkede leveransen bli kanselert og mottatt verdi blir ikke levert. */
export function leverForsinketMedMindreAnnenStrømLeverer<T>(forsinkelse: number, kanselerLeveranse: Observable<any>): MonoTypeOperatorFunction<T> {
    return switchMap(hendelse => timer(forsinkelse).pipe(
      takeUntil(kanselerLeveranse),
      map(() => hendelse),
    ));
}

/**
 *  Pipe-steg som leverer mottatt verdi forsinket med angitt 'forsinkelse',
 * med mindre angitt 'hendelseKilde' lever angitt 'hendelseNavn' i dette intervalle, da vil
 * den forsinkede leveransen bli kanselert og mottatt verdi blir ikke levert.
 *
 * Eks der `lukkNoe()` bli kalt når mus flyttes ut av elementA, MED MINDRE musen har blitt flyttet inn i elementB inne 200ms:
 * `fromEvent(elementA, 'mouseleave')
 *  .pipe(leverForsinketMedMindreAnnenHendelse(elementB, 'mouseenter', 200))
 *  .subscribe(()=> lukkNoe())`
 */
export function leverForsinketMedMindreAnnenHendelse<T>(forsinkelse: number, hendelseKilde: HTMLElement, hendelseNavn: string): MonoTypeOperatorFunction<T> {
    return leverForsinketMedMindreAnnenStrømLeverer(forsinkelse, fromEvent(hendelseKilde, hendelseNavn));
}

/** Pipesteg som ikke gjør noen verdens ting */
export function ingenOperasjon<T>(): MonoTypeOperatorFunction<T> {
    return kilde => kilde;
}

/** Pipe-steg som filterer bort alle verdier som ikke er lik angitt 'forventet' */
export function erLik<T>(forventet: T): MonoTypeOperatorFunction<T> {
    return filter(faktisk => faktisk === forventet);
}
